import { useState, useEffect, useMemo } from 'react';
import { generateVideoThumbnailFromUrl } from 'utils/generateVideoThumbnail';

interface VideoEntry {
  id: string;
  url: string;
}

// Module-level cache — survives component mount/unmount cycles
const MAX_CACHE_SIZE = 100;
const thumbnailCache = new Map<string, string>();

export const useVideoThumbnails = (videos: VideoEntry[]): Map<string, string> => {
  const [thumbnails, setThumbnails] = useState<Map<string, string>>(() => {
    // Initialize from cache for instant display on remount
    const initial = new Map<string, string>();
    videos.forEach(({ id }) => {
      const cached = thumbnailCache.get(id);
      if (cached) initial.set(id, cached);
    });
    return initial;
  });

  // Stabilize the dependency — only re-run when video ids/urls actually change
  const videoKey = useMemo(
    () => videos.map((v) => `${v.id}:${v.url}`).join('|'),
    [videos]
  );

  useEffect(() => {
    if (!videos.length) return;

    let cancelled = false;

    videos.forEach(({ id, url }) => {
      if (!url) return;

      if (thumbnailCache.has(id)) {
        // Already cached — ensure local state has it
        setThumbnails((prev) => {
          if (prev.has(id)) return prev;
          const updated = new Map(prev);
          updated.set(id, thumbnailCache.get(id)!);
          return updated;
        });
        return;
      }

      generateVideoThumbnailFromUrl(url)
        .then((dataUrl) => {
          if (cancelled) return;
          if (thumbnailCache.size >= MAX_CACHE_SIZE) {
            const firstKey = thumbnailCache.keys().next().value;
            if (firstKey) thumbnailCache.delete(firstKey);
          }
          thumbnailCache.set(id, dataUrl);
          setThumbnails((prev) => {
            const updated = new Map(prev);
            updated.set(id, dataUrl);
            return updated;
          });
        })
        .catch(() => {
          // Thumbnail generation failed — will show placeholder
        });
    });

    return () => { cancelled = true; };
  }, [videoKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return thumbnails;
};
